import { ERR_DEFAULT, UserEvents } from 'shared/consts';
import {
  cancelGroupVisit as cancelGroupVisitAction,
  cancelVisit as cancelVisitAction,
  createVisit as createVisitAction,
  fetchAllVisits as fetchAllVisitsAction,
  fetchPastVisits as fetchPastVisitsAction,
  fetchUpcomingVisits as fetchUpcomingVisitsAction,
  fetchVisit as fetchVisitAction,
  fetchVisitSeries as fetchVisitSeriesAction,
  fetchVisits as fetchVisitsAction,
  updateVisit as updateVisitAction,
  updateVisitSeries as updateVisitSeriesAction,
} from './actions';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { getCurrentBuildingDateTime, visitCanceled } from './util';
import { mapPaginatedVisits, mapVisit } from 'store/visits/mappers';
import {
  countDateDiffs,
  isMultiDayCreateResponse,
  mapMultiDayCreateResponseToVisit,
  toDateOnlyString,
} from './multi-day.utils';

import { Epic } from 'redux-observable';
import { RootAction } from 'store/actions';
import { RootDependencies } from 'store/dependencies';
import { RootState } from 'store/reducer';
import { VisitUiType, FetchVisitsParams, FetchSpecificVisitsActionParams } from './types';
import { getErrorCode } from 'store/utils';
import { isActionOf } from 'typesafe-actions';
import { of, EMPTY } from 'rxjs';
import { track } from '@hqo/web-tracking';
import { buildVisitDetailsPath, buildVisitsListPath } from 'utils/route-helpers';

/** Strip the client-only `background` flag so it never reaches the API request body. */
const toRequestParams = (
  payload: FetchSpecificVisitsActionParams,
): Omit<FetchSpecificVisitsActionParams, 'background'> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { background, ...rest } = payload;
  return rest;
};

export const fetchVisits: Epic<RootAction, RootAction, RootState, RootDependencies> = (action$) =>
  action$.pipe(
    filter(isActionOf(fetchVisitsAction)),
    map((action) => {
      const { type, ...actionParams } = action.payload;

      const filterParam = getCurrentBuildingDateTime(type);
      const payload = { ...actionParams, ...filterParam };

      switch (type) {
        case VisitUiType.PAST:
          return fetchPastVisitsAction.request(payload);
        case VisitUiType.UPCOMING:
          return fetchUpcomingVisitsAction.request(payload);
        case VisitUiType.ALL:
        default:
          return fetchAllVisitsAction.request(actionParams);
      }
    }),
  );

export const fetchUpcomingVisits: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(fetchUpcomingVisitsAction.request)),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      const { hostUuids, status, companyUuids, search } = state.visits.filters;
      const trimmedSearch = search?.trim();
      // `background` is a client-only signal for the loading reducer — never send it to the API.
      const visitParams = toRequestParams(payload);
      const fetchParams: FetchVisitsParams = {
        ...visitParams,
        ...(hostUuids.length > 0 && {
          host_uuids: hostUuids,
        }),
        ...(companyUuids.length > 0 && {
          host_company_uuids: companyUuids,
        }),
        ...(status.length > 0 && {
          statuses: status,
        }),
        ...(trimmedSearch && {
          q: trimmedSearch,
        }),
      } as FetchVisitsParams;

      return apiClient(state)
        .fetchVisits(fetchParams)
        .pipe(
          map(({ response }) =>
            fetchUpcomingVisitsAction.success({
              params: payload,
              result: mapPaginatedVisits(response, state.user.user),
            }),
          ),
          catchError((error: Error) =>
            of(
              fetchUpcomingVisitsAction.failure({
                params: payload,
                error,
                errorCode: getErrorCode(error),
              }),
            ),
          ),
        );
    }),
  );

export const fetchPastVisits: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(fetchPastVisitsAction.request)),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      const { hostUuids, status, companyUuids, search } = state.visits.filters;
      const trimmedSearch = search?.trim();
      // `background` is a client-only signal for the loading reducer — never send it to the API.
      const visitParams = toRequestParams(payload);
      const fetchParams: FetchVisitsParams = {
        ...visitParams,
        ...(hostUuids.length > 0 && {
          host_uuids: hostUuids,
        }),
        ...(companyUuids.length > 0 && {
          host_company_uuids: companyUuids,
        }),
        ...(status.length > 0 && {
          statuses: status,
        }),
        ...(trimmedSearch && {
          q: trimmedSearch,
        }),
      } as FetchVisitsParams;

      return apiClient(state)
        .fetchVisits(fetchParams)
        .pipe(
          map(({ response }) =>
            fetchPastVisitsAction.success({
              params: payload,
              result: mapPaginatedVisits(response, state.user.user),
            }),
          ),
          catchError((error: Error) =>
            of(
              fetchPastVisitsAction.failure({
                params: payload,
                error,
                errorCode: getErrorCode(error),
              }),
            ),
          ),
        );
    }),
  );

export const fetchAllVisits: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(fetchAllVisitsAction.request)),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      const { hostUuids, status, companyUuids, search } = state.visits.filters;
      const trimmedSearch = search?.trim();
      // `background` is a client-only signal for the loading reducer — never send it to the API.
      const visitParams = toRequestParams(payload);
      const fetchParams: FetchVisitsParams = {
        ...visitParams,
        ...(hostUuids.length > 0 && {
          host_uuids: hostUuids,
        }),
        ...(companyUuids.length > 0 && {
          host_company_uuids: companyUuids,
        }),
        ...(status.length > 0 && {
          statuses: status,
        }),
        ...(trimmedSearch && {
          q: trimmedSearch,
        }),
      } as FetchVisitsParams;

      return apiClient(state)
        .fetchVisits(fetchParams)
        .pipe(
          map(({ response }) =>
            fetchAllVisitsAction.success({
              params: payload,
              result: mapPaginatedVisits(response, state.user.user),
            }),
          ),
          catchError((error: Error) =>
            of(
              fetchAllVisitsAction.failure({
                params: payload,
                error,
                errorCode: getErrorCode(error),
              }),
            ),
          ),
        );
    }),
  );

export const fetchVisit: Epic<RootAction, RootAction, RootState, RootDependencies> = (action$, state$, { apiClient }) =>
  action$.pipe(
    filter(isActionOf(fetchVisitAction.request)),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      return apiClient(state)
        .fetchVisit(payload)
        .pipe(
          map(({ response }) =>
            fetchVisitAction.success({
              params: payload,
              result: response && mapVisit(response, state.user.user),
            }),
          ),
          catchError((error: Error) =>
            of(
              fetchVisitAction.failure({
                params: payload,
                error,
                errorCode: getErrorCode(error),
              }),
            ),
          ),
        );
    }),
  );

export const cancelVisit: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(cancelVisitAction.request)),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      return apiClient(state)
        .cancelVisit(payload)
        .pipe(
          map(({ response }) => {
            if (response.canceled) {
              visitCanceled(state?.uiMetadata?.data?.integration_name);
              return cancelVisitAction.success(payload);
            }

            return cancelVisitAction.failure({
              params: payload,
              error: new Error('Error canceling visit'),
              errorCode: ERR_DEFAULT,
            });
          }),
          catchError((error: Error) =>
            of(cancelVisitAction.failure({ params: payload, error, errorCode: getErrorCode(error) })),
          ),
        );
    }),
  );

export const cancelGroupVisit: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(cancelGroupVisitAction.request)),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      return apiClient(state)
        .cancelGroupVisit(payload)
        .pipe(
          map(({ response }) => {
            if (response.canceled) {
              visitCanceled(state?.uiMetadata?.data?.integration_name);
              return cancelGroupVisitAction.success(payload);
            }

            return cancelGroupVisitAction.failure({
              error: new Error('Error canceling group visit'),
              errorCode: ERR_DEFAULT,
            });
          }),
          catchError((error: Error) => of(cancelGroupVisitAction.failure({ error, errorCode: getErrorCode(error) }))),
        );
    }),
  );

export const cancelVisitRedirect: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { navigation },
) =>
  action$.pipe(
    filter(isActionOf([cancelVisitAction.success, cancelGroupVisitAction.success])),
    withLatestFrom(state$),
    tap(([action, state]) => {
      if (action.payload.skipPostCancelRedirect) {
        return;
      }
      // Redirect to the visits list
      if (state.config.buildingUuid) {
        const path = buildVisitsListPath({
          ownerType: 'building',
          ownerId: state.config.buildingUuid,
        });
        navigation.push(path);
      } else {
        navigation.push('/');
      }
    }),
    switchMap(() => EMPTY),
  );

export const createVisit: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(createVisitAction.request)),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      const { buildingId, visitDto } = payload;
      return apiClient(state)
        .createVisit({ buildingId, visitDto })
        .pipe(
          map(({ response }) => {
            const visitDates = visitDto.visit_dates ?? [];
            const isMultiDay = visitDates.length > 1;
            track(
              UserEvents.VISIT_SUBMITTED,
              {
                type: 'action',
                service_provider: state.uiMetadata?.data?.integration_name ?? 'UNKNOWN',
                visitors_added: visitDto.visitors.length,
                is_multi_day: isMultiDay,
                visit_days_count: isMultiDay ? visitDates.length : 1,
              },
              { sendToPendo: true, sendToHqoTracking: true },
            );
            const mappedVisit = isMultiDayCreateResponse(response)
              ? mapMultiDayCreateResponseToVisit(response, visitDto, state.user.user)
              : mapVisit(response, state.user.user);

            // Merge photo_uuid from request into response visitors (API may not return it)
            if (mappedVisit.visitors && visitDto.visitors) {
              mappedVisit.visitors = mappedVisit.visitors.map((visitor, index) => ({
                ...visitor,
                photo_uuid: visitor.photo_uuid || visitDto.visitors[index]?.photo_uuid || null,
              }));
            }

            if (isMultiDay && !mappedVisit.series_dates?.length) {
              mappedVisit.series_dates = visitDates;
            }

            return createVisitAction.success(mappedVisit);
          }),
          catchError((error: Error) => of(createVisitAction.failure({ error, errorCode: getErrorCode(error) }))),
        );
    }),
  );

export const createVisitRedirect: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { navigation },
) =>
  action$.pipe(
    filter(isActionOf(createVisitAction.success)),
    withLatestFrom(state$),
    tap(([{ payload }, state]) => {
      // Redirect to the created visit confirmation or details page
      if (state.config.buildingUuid) {
        const useV2UI = state.uiMetadata?.data?.ui_metadata?.visitor_management_new_ui === true;

        if (useV2UI) {
          const confirmationPath = buildVisitsListPath({
            ownerType: 'building',
            ownerId: state.config.buildingUuid,
          });
          if (state.visits.suppressVisitConfirmationRedirect) {
            navigation.push(`${confirmationPath}/view`);
          } else {
            navigation.push(`${confirmationPath}/view/${payload.id}/confirm`);
          }
        } else {
          // V1: Redirect to visit details page
          const path = buildVisitDetailsPath(
            {
              ownerType: 'building',
              ownerId: state.config.buildingUuid,
            },
            VisitUiType.UPCOMING,
            payload.id,
          );
          navigation.push(path);
        }
      } else {
        navigation.push('/');
      }
    }),
    switchMap(() => EMPTY),
  );

export const updateVisit: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(updateVisitAction.request)),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      return apiClient(state)
        .updateVisit(payload)
        .pipe(
          map(({ response }) => {
            const additionalOccurrences = payload.payload.additional_occurrences ?? [];
            const isMultiDayEdit = additionalOccurrences.length > 0;
            // Tracking-service allowlists VISIT_SUBMITTED, not VISIT_UPDATED — HQO track 422s.
            track(
              UserEvents.VISIT_UPDATED,
              {
                type: 'action',
                service_provider: state.uiMetadata?.data?.integration_name ?? 'UNKNOWN',
                is_multi_day_edit: isMultiDayEdit,
                dates_added_count: additionalOccurrences.length,
                dates_removed_count: 0,
              },
              { sendToPendo: true, sendToHqoTracking: false },
            );

            const mappedVisit = isMultiDayCreateResponse(response)
              ? mapMultiDayCreateResponseToVisit(
                  response,
                  {
                    ...payload.payload,
                    visitors: payload.payload.visitors ?? [],
                    visit_dates: undefined,
                  },
                  state.user.user,
                )
              : mapVisit(response, state.user.user);

            if (isMultiDayEdit) {
              const sourceDate = payload.payload.arrival_date
                ? toDateOnlyString(payload.payload.arrival_date)
                : undefined;
              mappedVisit.series_dates = [
                ...(sourceDate ? [sourceDate] : []),
                ...additionalOccurrences.map((occurrence) => toDateOnlyString(occurrence.arrival_time)),
              ];
            }

            return updateVisitAction.success({
              params: payload.params,
              result: mappedVisit,
              isActivityPane: payload.isActivityPane,
            });
          }),
          catchError((error: Error) =>
            of(updateVisitAction.failure({ params: payload.params, error, errorCode: getErrorCode(error) })),
          ),
        );
    }),
  );

export const fetchVisitSeries: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(fetchVisitSeriesAction.request)),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      return apiClient(state)
        .fetchVisitSeries(payload)
        .pipe(
          map(({ response }) =>
            fetchVisitSeriesAction.success({
              params: payload,
              result: response,
            }),
          ),
          catchError((error: Error) =>
            of(
              fetchVisitSeriesAction.failure({
                params: payload,
                error,
                errorCode: getErrorCode(error),
              }),
            ),
          ),
        );
    }),
  );

export const updateVisitSeries: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(updateVisitSeriesAction.request)),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      return apiClient(state)
        .updateVisitSeries(payload)
        .pipe(
          map(({ response }) => {
            const nextDates = (payload.payload.dates ?? []).map((date) => date.arrivalTime.slice(0, 10));
            const { datesAddedCount, datesRemovedCount } = countDateDiffs(payload.originalDates ?? [], nextDates);
            // Tracking-service allowlists VISIT_SUBMITTED, not VISIT_UPDATED — HQO track 422s.
            track(
              UserEvents.VISIT_UPDATED,
              {
                type: 'action',
                service_provider: state.uiMetadata?.data?.integration_name ?? 'UNKNOWN',
                is_multi_day_edit: true,
                dates_added_count: datesAddedCount,
                dates_removed_count: datesRemovedCount,
              },
              { sendToPendo: true, sendToHqoTracking: false },
            );

            const result: import('./types').VisitSeries =
              response && typeof response === 'object' && 'seriesUuid' in response
                ? (response as import('./types').VisitSeries)
                : {
                    seriesUuid: payload.params.seriesId,
                    dates: payload.payload.dates ?? [],
                  };

            return updateVisitSeriesAction.success({
              params: payload.params,
              result,
            });
          }),
          catchError((error: Error) =>
            of(
              updateVisitSeriesAction.failure({
                params: payload.params,
                error,
                errorCode: getErrorCode(error),
              }),
            ),
          ),
        );
    }),
  );

export const updateVisitRedirect: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { navigation },
) =>
  action$.pipe(
    filter(isActionOf(updateVisitAction.success)),
    withLatestFrom(state$),
    tap(([{ payload }, state]) => {
      if (state.config.buildingUuid) {
        const useV2UI = state.uiMetadata?.data?.ui_metadata?.visitor_management_new_ui === true;

        if (useV2UI) {
          const visitsPath = buildVisitsListPath({
            ownerType: 'building',
            ownerId: state.config.buildingUuid,
          });
          // In the activity-center pane the visits list is redundant — the pane already shows it —
          // so return to the visit's own pane view instead. Elsewhere, keep landing on the list.
          if (payload.isActivityPane) {
            navigation.push(`${visitsPath}/${payload.params.visitId}`);
          } else if (payload.result.series_dates && payload.result.series_dates.length > 1) {
            navigation.push(`${visitsPath}/view/${payload.result.id}/confirm`);
          } else {
            navigation.push(`${visitsPath}/view`);
          }
        } else {
          // V1: Redirect to the visit details page with the same visitUiType
          // Extract visitUiType from current URL if we're on an edit page
          // URL pattern: /visitor-registration/building/{ownerId}/visits/{visitUiType}/{visitId}/edit/{step}
          const { pathname } = window.location;
          const editRouteMatch = pathname.match(/\/visits\/(upcoming|past|all)\/([^/]+)\/edit/);
          const visitUiType = editRouteMatch ? (editRouteMatch[1] as unknown as VisitUiType) : VisitUiType.UPCOMING;

          const path = buildVisitDetailsPath(
            {
              ownerType: 'building',
              ownerId: state.config.buildingUuid,
            },
            visitUiType,
            payload.result.id,
          );
          navigation.push(path);
        }
      } else {
        navigation.push('/');
      }
    }),
    switchMap(() => EMPTY),
  );

export const updateVisitSeriesRedirect: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { navigation },
) =>
  action$.pipe(
    filter(isActionOf(updateVisitSeriesAction.success)),
    withLatestFrom(state$),
    tap(([, state]) => {
      if (state.config.buildingUuid) {
        const visitsPath = buildVisitsListPath({
          ownerType: 'building',
          ownerId: state.config.buildingUuid,
        });
        navigation.push(`${visitsPath}/view`);
      } else {
        navigation.push('/');
      }
    }),
    switchMap(() => EMPTY),
  );
